<script>
	import { slide } from "svelte/transition";

	import model from "../../stores/modelStore";
	import { registerPageComplete } from "../../tealium";
	import { projectSettings, dropZoneOptions } from "../../config";
	import { isValid } from "../../validation";

	import Form from "../../components/Form.svelte";
	import FormRowColumn from "../../components/FormRowColumn.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import DropZone from "../../components/DropZone.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	import ContactName from "./components/ContactName.svelte";
	import Employment from "./components/Employment.svelte";
	import OtherNames from "./components/OtherNames.svelte";
	import Family from "./components/Family.svelte";
	import Children from "./components/Children.svelte";

	// props
	export let persistUrl = null;
	export let pageName = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;

	let buttons = null;

	const options = dropZoneOptions($model.registrationToken);

	// component state updating
	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const relationshipWithSpouseOptions = [
		{
			label: "In a legal or customary marriage with the deceased person and that relationship had not ended?",
			value: "Married"
		},
		{
			label: "In a defacto relationship with the deceased person where you lived together as a couple for a period of at least two (2) years in a relationship, and the relationship had not ended?",
			value: "Defacto"
		}
	];

	const knowledgeOfWorkOptions = [
		{
			label: "The deceased person told me",
			value: "The deceased person told me"
		},
		{
			label: "I saw it",
			value: "I saw it"
		},
		{
			label: "Someone who knew the deceased person told me",
			value: "Someone who knew the deceased person told me"
		},
		{
			label: "I have seen records",
			value: "I have seen records"
		},
		{ label: "Other", value: "Other" }
	];

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {
			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	$: showknowledgeOfWorkOther = $model.claimDetails.knowledgeOfWork.includes("Other");

	const initialise = () => {};

	initialise();
</script>

<PageSection>
	<Form {disabled} id="claim-for-yourself-details" on:submit={handleSubmit}>
		<FormRowColumn rowClass="justify-content-center confirmation-hide">
			<h4 class="text-center">
				{@html projectSettings.classActionName}
			</h4>
			<h1 class="text-center">Part 4: Claim For Your Spouse</h1>
		</FormRowColumn>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<p class="confirmation-hide">
				Only complete this section if you are <b
					>registering for your Spouse (husband, wife or partner - married or defacto), who has passed away</b
				>
				(referred to as the "deceased person").
			</p>

			<FormRowColumn>
				<RadioGroup
					required
					{disabled}
					cols="12"
					name="relationshipWithSpouse"
					label="At the time of the deceased person passing were you:"
					bind:value={$model.spouseClaim.relationshipWithSpouse}
					optionList={relationshipWithSpouseOptions}
					separatorText="<b>OR</b>"
				/>
			</FormRowColumn>

			<p>
				If you are claiming for more than one deceased spouse, please fill in separate registration forms for
				each spouse.
			</p>

			<p class="label">What was the first and last name of the deceased person?</p>

			<ContactName
				required
				{disabled}
				bind:firstName={$model.claimDetails.deceased.firstName}
				bind:surname={$model.claimDetails.deceased.surname}
			/>

			<FormRowColumn>
				<TextInput
					{disabled}
					label="What was the maiden name (last name before they got married to you) of the deceased person (if applicable)?"
					bind:value={$model.claimDetails.deceased.maidenName}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<TextInput
					{disabled}
					maxlength="50"
					label="What was the skin name of the deceased person (if you know, and if they had one)?"
					bind:value={$model.claimDetails.deceased.skinName}
				/>
			</FormRowColumn>

			<OtherNames
				label="Did the deceased person have any other name(s)?"
				bind:hasOtherNames={$model.claimDetails.deceased.hasOtherNames}
				bind:otherNames={$model.claimDetails.deceased.otherNames}
				{disabled}
			/>

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="isAboriginalTorresStraitIslander"
					label="Was the deceased person Aboriginal or Torres Strait Islander?"
					bind:value={$model.claimDetails.deceased.isAboriginalTorresStraitIslander}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<TextInput
					{disabled}
					label="What was the deceased person's date of birth to the best of your knowledge?"
					sublabel="If you do not know their exact date of birth, you can give us an approximate date of birth or an approximate year of birth."
					bind:value={$model.claimDetails.deceased.dateOfBirth}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<TextInput
					{disabled}
					label="What was the deceased person's place of birth to the best of your knowledge?"
					sublabel="If you do not know their exact place of birth, you can give us an approximate place of birth."
					bind:value={$model.claimDetails.deceased.placeOfBirth}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<TextInput
					{disabled}
					label="What was the date the deceased person passed away?"
					sublabel="If you do not know the exact date, you can put the approximate date or year the deceased person passed away."
					bind:value={$model.claimDetails.deceased.dateOfDeath}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<TextInput
					{disabled}
					label="What was the deceased person's language group (if you know)?"
					bind:value={$model.claimDetails.deceased.languageGroup}
				/>
			</FormRowColumn>

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="hasIDDocuments"
					label="Do you have any documents showing the date of birth or date of death of the deceased person, or your relationship?"
					sublabel="(i.e. birth or death certificate for the deceased person; or your marriage certificate; or a letter showing both of your names)"
					bind:value={$model.claimDetails.deceased.hasIDDocuments}
				/>
			</FormRowColumn>

			{#if $model.claimDetails.deceased.hasIDDocuments == "Yes"}
				<div transition:slide|local>
					<p>
						Please upload copies of these documents to the Registration Form or text message, email or post
						(do not post original documents) them to us.
					</p>

					<FormRowColumn>
						<DropZone
							{disabled}
							{options}
							id="dz_spouse_id"
							filenamePrefix="SPOUSE_ID_"
							bind:documentList={$model.spouseIdDocumentList}
						></DropZone>
					</FormRowColumn>
				</div>
			{/if}

			<FormRowColumn>
				<TextInput
					{disabled}
					maxlength="50"
					label="When did you and the deceased person get married (if married) or start living together (if in a defacto relationship)?"
					sublabel="If you do not know the exact date, you can put an approximate date or year."
					bind:value={$model.spouseClaim.dateOfMarriage}
				/>
			</FormRowColumn>

			{#if $model.spouseClaim.relationshipWithSpouse == "Defacto"}
				<div transition:slide|local>
					<FormRowColumn>
						<TextInput
							{disabled}
							label="Where did you and the deceased person live together?"
							bind:value={$model.spouseClaim.whereLived}
						/>
					</FormRowColumn>
				</div>
			{/if}

			<Family
				{disabled}
				motherLabel="What was the name of the deceased person's mother"
				fatherLabel="What was the name of the deceased person's father"
				siblingLabel="What were the names of the deceased person’s siblings (brothers and sisters)"
				bind:mother={$model.claimDetails.deceased.mother}
				bind:father={$model.claimDetails.deceased.father}
				bind:siblings={$model.claimDetails.deceased.siblings}
			/>

			<Children
				{disabled}
				label="What are the names of the deceased person's children <span style='font-weight: 300'>(if they had any)</span>"
				bind:children={$model.spouseClaim.spouseChildren}
			/>

			<Children
				{disabled}
				label="What are the names of your children <span style='font-weight: 300'>(if you have any)</span>"
				bind:children={$model.spouseClaim.claimantChildren}
			/>

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="workedInNTBetweenDates"
					label="Did the deceased person work in the Northern Territory between 1 June 1933 and 12 November 1971?"
					bind:value={$model.claimDetails.workedInNTBetweenDates}
				/>
			</FormRowColumn>

			<p class="label mt-4">
				Please tell us information about the deceased person's work in the Northern Territory in the table
				below. For example, you can talk about the work they did at a station, a settlement or a mission (or
				more than one place).
			</p>

			<Employment {disabled} bind:employment={$model.claimDetails.employment} />

			<p>
				If you need more space to tell us about your work, please use Additional Information text box at the end
				of this form.
			</p>

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="littleOrNoWages"
					label="Did the deceased person receive little or no wages for their work (above)?"
					bind:value={$model.claimDetails.littleOrNoWages}
				/>
			</FormRowColumn>

			<CheckboxButtonList
				{disabled}
				name="knowledgeOfWork"
				label="How do you know this information about the deceased person's work:"
				bind:selected={$model.claimDetails.knowledgeOfWork}
				optionList={knowledgeOfWorkOptions}
			/>

			{#if showknowledgeOfWorkOther}
				<div transition:slide|local>
					<FormRowColumn>
						<TextInput
							{disabled}
							label="Please specify"
							bind:value={$model.claimDetails.howKnowWorkInformationOther}
						/>
					</FormRowColumn>
				</div>
			{/if}

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="haveWorkRecords"
					label="Do you have any records about the deceased persons' work?"
					bind:value={$model.claimDetails.haveWorkRecords}
				/>
			</FormRowColumn>

			{#if $model.claimDetails.haveWorkRecords == "Yes"}
				<div transition:slide|local>
					<p>
						Please upload copies of these documents or text message, email or post (do not post original
						documents) them to us (see contact details). You do not have to give us documents, but any
						documents you have may help with your registration and payment of compensation money to you.
					</p>

					<FormRowColumn>
						<DropZone
							{disabled}
							{options}
							id="dz_spouse_work_records"
							filenamePrefix="SPOUSE_WORK_"
							bind:documentList={$model.spouseWorkRecordsDocumentList}
						></DropZone>
					</FormRowColumn>
				</div>
			{/if}
		</div>

		<FormRowColumn rowClass="confirmation-hide">
			<NavigationButtons bind:buttons />
		</FormRowColumn>
	</Form>
</PageSection>
